// Stack Page - Display recommended stack with solution preview
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Brain, Wrench,
  CheckCircle2, AlertCircle, Clock, DollarSign,
  RefreshCw, Zap, Rocket, Shield, Settings,
  User, Bot, Play, Star, Edit3, X, Check, Loader2,
  Hammer, Cpu, Timer, Info, LogOut
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import useConfigStore from '../store/configStore';
import { useAuth } from '../contexts/AuthContext';
import { generateStackRecommendation } from '../services/api';
import {
  trackStackView,
  trackStackApprove,
  trackStackModify,
  trackStackRegenerate
} from '../services/analytics';

const statusColors = {
  mcp_ready: 'bg-green-100 text-green-700',
  api_available: 'bg-yellow-100 text-yellow-700',
  build_needed: 'bg-orange-100 text-orange-700'
};

const statusIcons = {
  mcp_ready: CheckCircle2,
  api_available: AlertCircle,
  build_needed: Clock
};

// Interview questions for editing (generic, not sector-specific)
const INTERVIEW_FIELDS = [
  {
    id: 'goal',
    label: 'What do you want to achieve?',
    type: 'text'
  },
  {
    id: 'expertise',
    label: 'Your Background',
    type: 'text'
  },
  {
    id: 'userCapabilities',
    label: 'Your DIY Skills',
    type: 'text',
    placeholder: 'e.g., Carpentry, Programming, Photography...'
  },
  {
    id: 'currentTools',
    label: 'Tools you currently use',
    type: 'text'
  },
  {
    id: 'budget',
    label: 'Monthly Budget',
    type: 'select',
    options: ['Free only', '$1-50/month', '$50-200/month', '$200+/month', 'Not sure yet']
  },
  {
    id: 'timeline',
    label: 'When do you need this?',
    type: 'select',
    options: ['ASAP', '1-2 weeks', '1-3 months', 'Just exploring']
  },
  {
    id: 'additionalContext',
    label: 'Additional Details',
    type: 'text'
  }
];

export default function Stack() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { goal, stack, interview, sessionId, setGoal, updateInterview, setStack, setCurrentStep } = useConfigStore();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // Da Vinci inventions for loading messages
  const vinciLoadingMessages = [
    { text: "Sketching your flying machine", emoji: "🚁" },
    { text: "Calibrating the aerial screw", emoji: "⚙️" },
    { text: "Mixing the perfect pigments", emoji: "🎨" },
    { text: "Consulting the Vitruvian Man", emoji: "🧍" },
    { text: "Winding the mechanical knight", emoji: "🤖" },
    { text: "Studying bird wing patterns", emoji: "🦅" },
    { text: "Drafting with mirror script", emoji: "✍️" },
    { text: "Calculating golden ratios", emoji: "📐" },
    { text: "Engineering the tank design", emoji: "🛡️" },
    { text: "Perfecting the parachute", emoji: "🪂" },
    { text: "Designing hydraulic systems", emoji: "💧" },
    { text: "Assembling the self-propelled cart", emoji: "🚗" },
  ];

  // Cycle through loading messages when regenerating
  useEffect(() => {
    if (!isRegenerating) {
      setLoadingMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % vinciLoadingMessages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [isRegenerating, vinciLoadingMessages.length]);

  // Redirect if no stack
  useEffect(() => {
    if (!goal || !stack) {
      navigate('/home');
    }
  }, [goal, stack, navigate]);

  // Track stack view
  useEffect(() => {
    if (stack) {
      const data = stack.stack || stack;
      const toolCount = data?.tools?.length || 0;
      const estimatedCost = stack?.totalCost || 'Unknown';
      trackStackView(toolCount, estimatedCost);
    }
  }, [stack]);

  const handleApprove = () => {
    const data = stack?.stack || stack;
    const toolCount = data?.tools?.length || 0;
    const estimatedCost = stack?.totalCost || 'Unknown';
    trackStackApprove(toolCount, estimatedCost);
    setCurrentStep('waitlist');
    navigate('/waitlist');
  };

  const handleModify = () => {
    trackStackModify();
    // Initialize edited values with current values
    // Convert arrays to string if needed
    const toolsValue = Array.isArray(interview.currentTools)
      ? interview.currentTools.join(', ')
      : interview.currentTools || '';

    const capabilitiesValue = Array.isArray(interview.userCapabilities)
      ? interview.userCapabilities.join(', ')
      : interview.userCapabilities || '';

    setEditedValues({
      goal: goal,
      expertise: interview.expertise || '',
      userCapabilities: capabilitiesValue,
      currentTools: toolsValue,
      budget: interview.budget || '',
      timeline: interview.timeline || '',
      additionalContext: interview.additionalContext || ''
    });
    setShowEditPanel(true);
  };

  const handleCancelEdit = () => {
    setShowEditPanel(false);
    setEditedValues({});
  };

  const handleSaveAndRegenerate = async () => {
    setIsRegenerating(true);

    // Track which fields were changed
    const changedFields = [];
    if (editedValues.goal !== goal) changedFields.push('goal');
    if (editedValues.expertise !== interview.expertise) changedFields.push('expertise');
    if (editedValues.budget !== interview.budget) changedFields.push('budget');
    if (editedValues.timeline !== interview.timeline) changedFields.push('timeline');
    trackStackRegenerate(changedFields);

    // Convert strings to arrays for API
    const toolsArray = typeof editedValues.currentTools === 'string'
      ? editedValues.currentTools.split(',').map(t => t.trim()).filter(Boolean)
      : editedValues.currentTools || [];

    const capabilitiesArray = typeof editedValues.userCapabilities === 'string'
      ? editedValues.userCapabilities.split(',').map(c => c.trim()).filter(Boolean)
      : editedValues.userCapabilities || [];

    // Update store with edited values
    if (editedValues.goal !== goal) {
      setGoal(editedValues.goal);
    }
    updateInterview({
      expertise: editedValues.expertise,
      userCapabilities: capabilitiesArray,
      currentTools: toolsArray,
      budget: editedValues.budget,
      timeline: editedValues.timeline,
      additionalContext: editedValues.additionalContext
    });

    try {
      // Regenerate stack with new values (include projectScope to avoid Firestore undefined error)
      const result = await generateStackRecommendation({
        sessionId,
        goal: editedValues.goal,
        projectScope: interview.projectScope || 'venture',
        expertise: editedValues.expertise,
        userCapabilities: capabilitiesArray,
        currentTools: toolsArray,
        budget: editedValues.budget,
        timeline: editedValues.timeline,
        additionalContext: editedValues.additionalContext
      });

      setStack(result);
      setShowEditPanel(false);
    } catch (error) {
      console.error('Regeneration error:', error);
      alert('Failed to regenerate stack. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setEditedValues(prev => ({ ...prev, [fieldId]: value }));
  };

  if (!stack) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Handle both API response formats
  const stackData = stack.stack || stack;
  const aiComponent = stackData.ai || { name: 'Claude', role: 'AI reasoning', status: 'ready' };
  const tools = stackData.tools || [];
  const explanation = stack.explanation || 'This stack is optimized for your use case.';
  const totalCost = stack.totalCost || '$20-50/month';
  const readiness = stack.readiness || 'high';
  const deployTime = stack.deployTime || '1-2 days';

  // Solution Preview from API
  const solutionPreview = stack.solutionPreview || {
    title: 'Your AI Assistant',
    description: 'An intelligent assistant tailored to your workflow',
    workflow: [
      { step: 1, userAction: 'Describe what you need', systemResponse: 'AI analyzes and takes action' },
      { step: 2, userAction: 'Review the results', systemResponse: 'Data is organized for you' },
      { step: 3, userAction: 'Ask follow-up questions', systemResponse: 'AI provides insights' }
    ],
    dailyBenefits: ['Save hours on manual tasks', 'Get instant answers', 'Stay organized']
  };

  // Task Breakdown from API
  const taskBreakdown = stack.taskBreakdown || null;
  const hasDiyTasks = taskBreakdown?.diyTasks?.length > 0;
  const hasAiTasks = taskBreakdown?.aiTasks?.length > 0;

  // Effort badge colors
  const effortColors = {
    easy: 'bg-green-100 text-green-700',
    medium: 'bg-yellow-100 text-yellow-700',
    hard: 'bg-red-100 text-red-700'
  };

  // Calculate cost breakdown
  const parseToolCost = (costStr) => {
    if (!costStr) return { min: 0, max: 0, isFree: true };
    const lower = costStr.toLowerCase();
    if (lower.includes('free')) return { min: 0, max: 0, isFree: true };
    const match = costStr.match(/\$(\d+)(?:-(\d+))?/);
    if (match) {
      return {
        min: parseInt(match[1]),
        max: parseInt(match[2] || match[1]),
        isFree: false
      };
    }
    return { min: 0, max: 0, isFree: false };
  };

  const toolCosts = tools.map(t => ({
    name: t.name,
    ...parseToolCost(t.cost),
    costLabel: t.cost || 'Free'
  }));

  const freeTools = toolCosts.filter(t => t.isFree);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/configure')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded flex items-center justify-center">
              <GoldenSpiral className="w-4 h-4" color="white" />
            </div>
            <span className="font-medium">VinciKit</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full text-green-700 text-sm font-medium mb-4">
              <Zap className="w-4 h-4" />
              Your Custom AI Solution is Ready
            </div>
            <h1 className="text-3xl font-bold mb-2 text-gray-900">
              {solutionPreview.title}
            </h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              {solutionPreview.description}
            </p>
          </motion.div>

          {/* Solution Preview - How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-6 mb-8 text-white"
          >
            <div className="flex items-center gap-2 mb-6">
              <Play className="w-5 h-5" />
              <h2 className="font-semibold text-lg">See How It Works</h2>
            </div>

            <div className="space-y-6">
              {solutionPreview.workflow.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="relative"
                >
                  {/* Step number */}
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      {item.step}
                    </div>

                    <div className="flex-1 space-y-3">
                      {/* User action */}
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <User className="w-3 h-3 text-white" />
                        </div>
                        <div className="bg-white/10 backdrop-blur rounded-lg rounded-tl-none px-4 py-2 flex-1">
                          <p className="text-sm font-medium text-blue-100">You:</p>
                          <p className="text-white">{item.userAction}</p>
                        </div>
                      </div>

                      {/* System response */}
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Bot className="w-3 h-3 text-white" />
                        </div>
                        <div className="bg-white/20 backdrop-blur rounded-lg rounded-tl-none px-4 py-2 flex-1">
                          <p className="text-sm font-medium text-green-200">AI Response:</p>
                          <p className="text-white">{item.systemResponse}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Connector line */}
                  {index < solutionPreview.workflow.length - 1 && (
                    <div className="absolute left-4 top-10 w-0.5 h-full bg-white/20" style={{ height: 'calc(100% - 32px)' }} />
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Task Breakdown - DIY vs AI */}
          {taskBreakdown && (hasDiyTasks || hasAiTasks) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
            >
              <div className="flex items-center gap-2 mb-6">
                <Hammer className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Your Task Breakdown</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* DIY Tasks - What You'll Do */}
                {hasDiyTasks && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">What You'll Do</h4>
                        <p className="text-xs text-gray-500">Using your skills</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {taskBreakdown.diyTasks.map((task, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.05 }}
                          className="bg-purple-50 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <p className="font-medium text-gray-900 text-sm">{task.task}</p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${effortColors[task.effort] || effortColors.medium}`}>
                                {task.effort}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              {task.timeEstimate}
                            </span>
                          </div>
                          {task.reason && (
                            <p className="text-xs text-purple-700 mt-2">{task.reason}</p>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Tasks - What AI Handles */}
                {hasAiTasks && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Cpu className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">What AI Handles</h4>
                        <p className="text-xs text-gray-500">Automated for you</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {taskBreakdown.aiTasks.map((task, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.05 }}
                          className="bg-blue-50 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <p className="font-medium text-gray-900 text-sm">{task.task}</p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${effortColors[task.effort] || effortColors.easy}`}>
                                {task.effort}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              {task.timeEstimate}
                            </span>
                            {task.tool && (
                              <span className="flex items-center gap-1">
                                <Bot className="w-3 h-3" />
                                {task.tool}
                              </span>
                            )}
                          </div>
                          {task.reason && (
                            <p className="text-xs text-blue-700 mt-2">{task.reason}</p>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Summary stats */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-center gap-8 text-sm">
                {hasDiyTasks && (
                  <div className="text-center">
                    <p className="font-semibold text-purple-600">{taskBreakdown.diyTasks.length}</p>
                    <p className="text-xs text-gray-500">Tasks you'll do</p>
                  </div>
                )}
                {hasAiTasks && (
                  <div className="text-center">
                    <p className="font-semibold text-blue-600">{taskBreakdown.aiTasks.length}</p>
                    <p className="text-xs text-gray-500">Tasks AI handles</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Daily Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-gray-900">What You'll Experience Daily</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {solutionPreview.dailyBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 + index * 0.05 }}
                  className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg"
                >
                  <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">{benefit}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Your AI System - Expanded Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
          >
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-6">
              Powered By
            </h3>

            <div className="grid sm:grid-cols-2 gap-4">
              {/* AI Component Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Brain className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{aiComponent.name}</h4>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        Ready
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{aiComponent.model || 'AI Reasoning Engine'}</p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {aiComponent.description || aiComponent.role || 'Advanced AI for reasoning, analysis, and automation tasks.'}
                </p>

                <div className="flex items-center justify-between text-xs mb-3">
                  <span className="text-gray-500">Cost</span>
                  <span className="font-medium text-gray-900">{aiComponent.cost || 'Usage-based'}</span>
                </div>

                {aiComponent.capabilities && aiComponent.capabilities.length > 0 && (
                  <div className="pt-3 border-t border-blue-100">
                    <p className="text-xs font-medium text-gray-500 mb-2">CAPABILITIES</p>
                    <div className="flex flex-wrap gap-1.5">
                      {aiComponent.capabilities.slice(0, 4).map((cap, i) => (
                        <span key={i} className="px-2 py-1 bg-white/80 text-blue-700 text-xs rounded-full">
                          {cap}
                        </span>
                      ))}
                      {aiComponent.capabilities.length > 4 && (
                        <span className="px-2 py-1 bg-white/60 text-gray-500 text-xs rounded-full">
                          +{aiComponent.capabilities.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Tool Cards */}
              {tools.map((tool, index) => {
                const status = tool.status || 'api_available';
                const StatusIcon = statusIcons[status] || AlertCircle;
                const statusLabel = tool.statusLabel || (status === 'mcp_ready' ? 'MCP Ready' : status === 'api_available' ? 'API Available' : 'Setup Required');

                // Determine card background based on status
                const cardBg = status === 'mcp_ready'
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-100'
                  : status === 'api_available'
                  ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-100'
                  : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-100';

                const iconBg = status === 'mcp_ready'
                  ? 'bg-green-100'
                  : status === 'api_available'
                  ? 'bg-amber-100'
                  : 'bg-orange-100';

                const iconColor = status === 'mcp_ready'
                  ? 'text-green-600'
                  : status === 'api_available'
                  ? 'text-amber-600'
                  : 'text-orange-600';

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.05 }}
                    className={`rounded-xl border p-5 hover:shadow-md transition-shadow ${cardBg}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <Wrench className={`w-6 h-6 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-gray-900">{tool.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium flex items-center gap-1 ${statusColors[status]}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusLabel}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{tool.api || tool.category || 'Integration'}</p>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {tool.description || tool.role || 'Provides specialized functionality for your project.'}
                    </p>

                    <div className="flex items-center justify-between text-xs mb-3">
                      <span className="text-gray-500">Cost</span>
                      <span className="font-medium text-gray-900">{tool.cost || 'Free'}</span>
                    </div>

                    {tool.features && tool.features.length > 0 && (
                      <div className="pt-3 border-t border-gray-200/50">
                        <p className="text-xs font-medium text-gray-500 mb-2">FEATURES</p>
                        <div className="flex flex-wrap gap-1.5">
                          {tool.features.slice(0, 4).map((feature, i) => (
                            <span key={i} className="px-2 py-1 bg-white/80 text-gray-700 text-xs rounded-full">
                              {feature}
                            </span>
                          ))}
                          {tool.features.length > 4 && (
                            <span className="px-2 py-1 bg-white/60 text-gray-500 text-xs rounded-full">
                              +{tool.features.length - 4} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            <p className="text-sm text-gray-500 mt-6 text-center">{explanation}</p>
          </motion.div>

          {/* Cost & Timeline Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-gray-900">Investment Summary</h3>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              {/* Monthly Running Costs */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Monthly Running Costs</p>
                <p className="text-2xl font-bold text-gray-900">{totalCost}</p>
                <p className="text-xs text-gray-500 mt-2">Tools & API usage</p>
                {freeTools.length > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    {freeTools.length} free integrations included
                  </p>
                )}
              </div>

              {/* One-time Setup/Materials */}
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">One-time Setup</p>
                <p className="text-2xl font-bold text-gray-900">$200-500</p>
                <p className="text-xs text-gray-500 mt-2">Implementation & configuration</p>
                <p className="text-xs text-amber-600 mt-1">
                  Early access pricing
                </p>
              </div>
            </div>

            {/* Total Project Cost Estimation */}
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
              <p className="text-sm font-medium text-gray-700 mb-3">Estimated Total Project Cost (First Year)</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Hobby/Personal */}
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-purple-600" />
                    </div>
                    <span className="font-medium text-gray-900">Hobby / Personal</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-600">$200-800</p>
                  <ul className="mt-2 space-y-1 text-xs text-gray-500">
                    <li>• Setup: $200-300</li>
                    <li>• Monthly: $0-40 × 12 = $0-480</li>
                    <li>• Free tier tools prioritized</li>
                    <li>• Self-managed maintenance</li>
                  </ul>
                </div>

                {/* Scale/Business */}
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <Rocket className="w-3 h-3 text-blue-600" />
                    </div>
                    <span className="font-medium text-gray-900">Scale / Business</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">$1,500-5,000</p>
                  <ul className="mt-2 space-y-1 text-xs text-gray-500">
                    <li>• Setup: $500-1,500</li>
                    <li>• Monthly: $100-300 × 12 = $1,200-3,600</li>
                    <li>• Premium API access</li>
                    <li>• Priority support included</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Quick stats row */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900 capitalize">{readiness}</span>
                </div>
                <p className="text-xs text-gray-500">Readiness level</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-900">{deployTime}</span>
                </div>
                <p className="text-xs text-gray-500">Setup time</p>
              </div>
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8"
          >
            <h3 className="font-semibold text-amber-900 mb-3">What Happens Next?</h3>
            <ol className="space-y-2 text-sm text-amber-800">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">1</span>
                <span>Approve this solution and join our early access waitlist</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">2</span>
                <span>We'll build and configure everything for you</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">3</span>
                <span>Get notified when your solution is ready to use</span>
              </li>
            </ol>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={handleApprove}
              className="px-8 py-4 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25"
            >
              <Rocket className="w-5 h-5" />
              I Want This! Get Early Access
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleModify}
              className="px-6 py-4 bg-white text-gray-700 font-medium rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Refine My Requirements
            </button>
          </motion.div>
        </div>
      </main>

      {/* Edit Panel Modal */}
      <AnimatePresence>
        {showEditPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={handleCancelEdit}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Edit Your Requirements</h2>
                  <p className="text-sm text-gray-500">Update any field and regenerate your solution</p>
                </div>
                <button
                  onClick={handleCancelEdit}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-4 overflow-y-auto max-h-[60vh] space-y-5">
                {INTERVIEW_FIELDS.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label}
                    </label>

                    {field.type === 'text' && (
                      <input
                        type="text"
                        value={editedValues[field.id] || ''}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={field.placeholder || (field.id === 'goal' ? 'What do you want to achieve?' : field.id === 'currentTools' ? 'e.g., Excel, Notion, pen and paper...' : 'Optional details...')}
                      />
                    )}

                    {field.type === 'select' && (
                      <div className="flex flex-wrap gap-2">
                        {field.options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleFieldChange(field.id, option)}
                            className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                              editedValues[field.id] === option
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Modal Footer - Show Da Vinci loading or buttons */}
              {isRegenerating ? (
                <div className="px-6 py-8 border-t border-gray-100">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <GoldenSpiral className="w-10 h-10" color="white" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={loadingMessageIndex}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="text-gray-700 flex items-center gap-2 text-lg"
                        >
                          <span>{vinciLoadingMessages[loadingMessageIndex].emoji}</span>
                          <span>{vinciLoadingMessages[loadingMessageIndex].text}...</span>
                        </motion.span>
                      </AnimatePresence>
                    </div>
                    <p className="text-sm text-gray-500">This may take a moment</p>
                  </div>
                </div>
              ) : (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAndRegenerate}
                    disabled={!editedValues.goal?.trim()}
                    className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate Solution
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
